import { useEffect, useMemo, useState } from "react";
import "./Style.css";
import "swiper/css";
import { Button, ButtonGroup, Col, Container, Modal, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Spinner from "react-bootstrap/Spinner";
import Style from "../../UserStyle.module.css";
import TimeAgo from "../../components/timeAgo/TimeAgo";
import { useSelector } from "react-redux";
import BreadCrumb from "../../components/breadcrumb/BreadCrumb";
import {
  FaArrowLeft,
  FaCheck,
  FaDownload,
  FaExternalLinkAlt,
  FaFileImage,
  FaFilePdf,
  FaPause,
  FaTimes,
} from "react-icons/fa";
import Notification, {
  showToast,
} from "../../../components/common/notification/Notification";
import axios from "axios";
import AdminHeader from "../../common/Header";
import Avatar from "../../components/notes/Avatar";
import "./partnerItem.css";

const DEFAULT_USER_DETAILS = {
  first_name: "",
  last_name: "",
  email_address: "",
  whatsapp_mobile_number: "",
  service_name: "",
  city: "",
  state: "",
  documents: [],
  status: "",
  created_on: "",
  userType: {
    name: "",
  },
  active: false,
  photo: "",
};

const normalizeStatus = (status = "") => status.trim().toUpperCase();

const buildAssetUrl = (assetBaseUrl, value) => {
  if (!value) {
    return "";
  }

  if (/^(?:https?:)?\/\//i.test(value) || value.startsWith("data:") || value.startsWith("blob:")) {
    return value;
  }

  if (!assetBaseUrl) {
    return value;
  }

  return `${assetBaseUrl.replace(/\/+$/, "")}/${String(value).replace(/^\/+/, "")}`;
};

const getDocumentName = (document) => {
  if (document?.name?.trim()) {
    return document.name.trim();
  }

  if (!document?.document_url) {
    return "Uploaded document";
  }

  const path = document.document_url.split("?")[0];
  const fileName = path.split("/").pop();
  return fileName || "Uploaded document";
};

const getDocumentExtension = (document) => {
  const fileName = getDocumentName(document).toLowerCase();
  const extension = fileName.split(".").pop();
  return extension || "";
};

const isPdfDocument = (document) => getDocumentExtension(document) === "pdf";

const isImageDocument = (document) =>
  ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "avif"].includes(
    getDocumentExtension(document)
  );

const normalizeDocuments = (documents, assetBaseUrl) => {
  const seen = new Set();

  return (Array.isArray(documents) ? documents : [])
    .map((document, index) => {
      const url = buildAssetUrl(assetBaseUrl, document?.document_url);
      const name = getDocumentName(document);

      return {
        id: `${url || name}-${index}`,
        name,
        url,
        isPdf: isPdfDocument(document),
        isImage: isImageDocument(document),
      };
    })
    .filter((document) => {
      const key = `${document.url}|${document.name}`;

      if (!document.url || seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
};

const extractPartnerFromPayload = (payload, partnerId) => {
  const targetId = Number(partnerId);

  if (payload?.user) {
    return {
      partner: payload.user,
      assetBaseUrl: payload.assetUrl || "",
    };
  }

  if (Array.isArray(payload?.users)) {
    const matchedPartner =
      payload.users.find((user) => Number(user?.id) === targetId) ||
      payload.users[0] ||
      null;

    return {
      partner: matchedPartner,
      assetBaseUrl: payload.assetUrl || "",
    };
  }

  return {
    partner: null,
    assetBaseUrl: payload?.assetUrl || "",
  };
};

const mapPartnerToViewModel = (user, assetBaseUrl) => ({
  first_name: user?.first_name || "",
  last_name: user?.last_name || "",
  email_address: user?.email_address || "",
  whatsapp_mobile_number:
    user?.whatsapp_mobile_number || user?.mobile_number || "",
  service_name: user?.service_name || "",
  city: user?.city || user?.cities?.[0]?.name || "",
  state:
    user?.state ||
    user?.states?.[0]?.name ||
    user?.cities?.[0]?.state?.name ||
    "",
  documents: normalizeDocuments(user?.documents, assetBaseUrl),
  status: user?.status || "",
  created_on: user?.created_on || "",
  userType: {
    name: user?.userType?.name || "",
  },
  active: Boolean(user?.active),
  photo: buildAssetUrl(assetBaseUrl, user?.photo),
});

const PartnerItem = () => {
  const navigate = useNavigate();
  const loggedInUser = useSelector((state) => state.auth.user);
  const token = loggedInUser?.refreshToken || loggedInUser?.token;
  const { partnerId } = useParams();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [userDetails, setUserDetails] = useState(DEFAULT_USER_DETAILS);

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const assetBaseUrl = process.env.REACT_APP_ASSET_URL || "";

  useEffect(() => {
    const fetchPartnerData = async () => {
      try {
        setIsLoading(true);

        const [userResponse, subscriptionResponse] = await Promise.allSettled([
          axios.get(`${baseUrl}/admin/user/id/${partnerId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
          axios.get(`${baseUrl}/payments/subscriptions/user/${partnerId}`),
        ]);

        if (userResponse.status !== "fulfilled") {
          throw userResponse.reason;
        }

        const payload = userResponse.value?.data ?? {};
        let { partner, assetBaseUrl: responseAssetBaseUrl } = extractPartnerFromPayload(
          payload,
          partnerId
        );

        if (!partner) {
          const fallbackListResponse = await axios.post(
            `${baseUrl}/admin/users`,
            {
              page_number: 1,
              page_size: 200,
              order_by: "DSC",
              user_type: "PARTNER",
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const fallbackPayload = fallbackListResponse?.data ?? {};
          const extractedFallback = extractPartnerFromPayload(
            fallbackPayload,
            partnerId
          );
          partner = extractedFallback.partner;
          responseAssetBaseUrl =
            extractedFallback.assetBaseUrl || responseAssetBaseUrl;
        }

        if (!partner) {
          throw new Error(`Partner ${partnerId} not found in API response`);
        }

        const resolvedAssetBaseUrl = responseAssetBaseUrl || assetBaseUrl;

        setUserDetails(mapPartnerToViewModel(partner, resolvedAssetBaseUrl));

        if (subscriptionResponse.status === "fulfilled") {
          setSubscriptionDetails(subscriptionResponse.value?.data ?? null);
        } else {
          setSubscriptionDetails(null);
          console.error("Error fetching subscription details:", subscriptionResponse.reason);
        }
      } catch (error) {
        console.error("Error fetching partner data:", error);
        showToast("Unable to load partner details right now.", {
          type: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (partnerId && token) {
      fetchPartnerData();
    }
  }, [assetBaseUrl, baseUrl, partnerId, token]);

  const breadcrumbItems = [
    { label: "", link: "/admin", icon: "fa-solid fa-house" },
    { label: "Master", link: "/partners" },
    { label: "Partner", link: "/partners", active: true },
    {
      label: userDetails?.first_name || "Partner Details",
      link: `/partners/${partnerId}`,
      active: true,
    },
  ];

  const normalizedStatus = useMemo(
    () => normalizeStatus(userDetails.status),
    [userDetails.status]
  );
  const canAcceptPartner = normalizedStatus !== "APPROVE";

  const handleModalOpen = (action) => {
    setModalAction(action);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleReviewAction = async () => {
    try {
      const payload = {
        id: Number(partnerId),
        first_name: userDetails.first_name,
        last_name: userDetails.last_name,
        status: modalAction,
      };

      const response = await axios.put(`${baseUrl}/admin/editUser`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.statusType === "SUCCESS") {
        setUserDetails((current) => ({
          ...current,
          status: modalAction,
        }));

        showToast(`User ${modalAction.toLowerCase()} successfully`, {
          type: "success",
        });

        setTimeout(() => {
          navigate("/partners");
        }, 1200);
      } else {
        showToast("Unable to update partner status.", {
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error updating partner status:", error);
      showToast("Some error occurred while updating the partner.", {
        type: "error",
      });
    } finally {
      handleModalClose();
    }
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedImage(null);
  };

  return (
    <>
      <Notification />
      {isLoading ? (
        <div className={Style.spinner}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <div className="content-body2 partner-review-page">
          <Container>
            <BreadCrumb items={breadcrumbItems} />

            <Row>
              <Col lg={3} md={4} sm={12}>
                <AdminHeader />
              </Col>
              <Col lg={9} md={8} sm={12} className="mainCardAreaInside">
                <div className="card rounded-3 mb-4 partner-review-page__shell">
                  <div className="container">
                    <div className="card shadow border-0 rounded-4 overflow-hidden partner-review-page__card">
                      <div className="container-fluid bg-light rounded-4 shadow-sm py-4 px-3 px-md-5 mb-4 partner-review-page__hero">
                        <div className="row align-items-center">
                          <div className="col-12 col-md-auto text-center mb-3 mb-md-0">
                            <Avatar
                              icon={userDetails.first_name}
                              src={userDetails.photo}
                              size={100}
                              className="rounded-circle shadow-sm"
                            />
                          </div>

                          <div className="col text-center text-md-start">
                            <div className="d-flex flex-column flex-md-row align-items-center justify-content-center justify-content-md-between gap-3">
                              <div>
                                <h2 className="mb-1 partner-review-page__title">
                                  {userDetails.first_name} {userDetails.last_name}
                                  {userDetails.status ? (
                                    <span
                                      className={`badge ms-2 ${
                                        normalizedStatus === "NEW"
                                          ? "bg-warning text-dark"
                                          : normalizedStatus === "APPROVE"
                                          ? "bg-success"
                                          : normalizedStatus === "REJECT"
                                          ? "bg-danger"
                                          : "bg-secondary"
                                      }`}
                                    >
                                      {normalizedStatus}
                                    </span>
                                  ) : null}
                                </h2>
                                <p className="text-muted mb-0">
                                  {userDetails.userType?.name || "User"}
                                </p>
                                <p className="text-muted mb-0 partner-review-page__meta">
                                  Joined{" "}
                                  {userDetails.created_on ? (
                                    <TimeAgo timestamp={userDetails.created_on} />
                                  ) : (
                                    "recently"
                                  )}
                                </p>
                              </div>

                              <div className="partner-review-page__summary">
                                <span className="partner-review-page__summary-label">
                                  Review status
                                </span>
                                <strong className="partner-review-page__summary-value">
                                  {normalizedStatus || "PENDING"}
                                </strong>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="card-body px-4">
                        <div className="row gy-4">
                          <SectionHeader
                            icon="bi-person-lines-fill"
                            title="Contact Info"
                          />
                          <InfoItem
                            icon="bi-envelope"
                            label="Email"
                            value={userDetails.email_address}
                          />
                          <InfoItem
                            icon="bi-phone"
                            label="Phone"
                            value={
                              userDetails.whatsapp_mobile_number
                                ? `+91 ${userDetails.whatsapp_mobile_number}`
                                : "Not provided"
                            }
                          />

                          {userDetails.service_name ? (
                            <InfoItem
                              icon="bi-briefcase"
                              label="Service"
                              value={userDetails.service_name}
                            />
                          ) : null}

                          <SectionHeader icon="bi-geo-alt" title="Location" />
                          <InfoItem
                            icon="bi-building"
                            label="City"
                            value={userDetails.city || "N/A"}
                          />
                          <InfoItem
                            icon="bi-map"
                            label="State"
                            value={userDetails.state || "N/A"}
                          />

                          <SectionHeader
                            icon="bi-file-earmark-text"
                            title="Documents"
                          />
                          {userDetails.documents.length > 0 ? (
                            <div className="row g-3">
                              {userDetails.documents.map((document) => (
                                <div key={document.id} className="col-12 col-md-6 col-xl-4">
                                  <article className="partner-document-card">
                                    <div className="partner-document-card__preview">
                                      {document.isImage ? (
                                        <img
                                          src={document.url}
                                          alt={document.name}
                                          className="partner-document-card__image"
                                          onClick={() => handleImageClick(document.url)}
                                        />
                                      ) : (
                                        <div className="partner-document-card__file-fallback">
                                          {document.isPdf ? (
                                            <FaFilePdf size={32} />
                                          ) : (
                                            <FaFileImage size={32} />
                                          )}
                                          <span>
                                            {document.isPdf ? "PDF document" : "Open file"}
                                          </span>
                                        </div>
                                      )}
                                    </div>

                                    <div className="partner-document-card__body">
                                      <h6 className="partner-document-card__name">
                                        {document.name}
                                      </h6>
                                      <div className="partner-document-card__actions">
                                        {document.isImage ? (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-primary"
                                            onClick={() => handleImageClick(document.url)}
                                          >
                                            <FaExternalLinkAlt />
                                            <span>Preview</span>
                                          </button>
                                        ) : (
                                          <a
                                            href={document.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="btn btn-sm btn-outline-primary"
                                          >
                                            <FaExternalLinkAlt />
                                            <span>Open</span>
                                          </a>
                                        )}
                                        <a
                                          href={document.url}
                                          target="_blank"
                                          rel="noreferrer"
                                          download
                                          className="btn btn-sm btn-outline-secondary"
                                        >
                                          <FaDownload />
                                          <span>Download</span>
                                        </a>
                                      </div>
                                    </div>
                                  </article>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="col-12">
                              <div className="alert alert-warning mb-0" role="alert">
                                No documents available for this partner yet.
                              </div>
                            </div>
                          )}

                          <SectionHeader
                            icon="bi-credit-card"
                            title="Subscription Details"
                          />
                          {subscriptionDetails?.subscriptionPlanActive ? (
                            <div className="card shadow-sm border-0 rounded-3 bg-light p-4 mt-2">
                              <h5 className="mb-3">Subscription Status</h5>
                              <div className="mb-3">
                                <span
                                  className={`badge ${
                                    subscriptionDetails.statusType === "SUCCESS"
                                      ? "bg-success"
                                      : "bg-danger"
                                  } text-white`}
                                >
                                  active
                                </span>
                              </div>
                              <div className="row">
                                <div className="col-12 col-md-6">
                                  <p className="text-muted mb-1">
                                    <strong>Plan Name:</strong>{" "}
                                    {subscriptionDetails.subscriptionPlan?.name}
                                  </p>
                                </div>
                                <div className="col-12 col-md-6">
                                  <p className="text-muted mb-1">
                                    <strong>Remaining Days:</strong>{" "}
                                    {subscriptionDetails.effectiveSubscriptionDays}
                                  </p>
                                </div>
                                <div className="col-12 col-md-6">
                                  <p className="text-muted mb-1">
                                    <strong>Duration (Days):</strong>{" "}
                                    {subscriptionDetails.durationInDays}
                                  </p>
                                </div>
                                <div className="col-12 col-md-6">
                                  <p className="text-muted mb-1">
                                    <strong>Price:</strong> Rs.
                                    {subscriptionDetails.subscriptionPlan?.price}
                                  </p>
                                </div>
                                <div className="col-12 col-md-6">
                                  <p className="text-muted mb-1">
                                    <strong>GST:</strong> Rs.
                                    {subscriptionDetails.subscriptionPlan?.gst}
                                  </p>
                                </div>
                                <div className="col-12 col-md-6">
                                  <p className="text-muted mb-1">
                                    <strong>Total Price:</strong> Rs.
                                    {subscriptionDetails.subscriptionPlan?.total_price}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="col-12">
                              <div className="alert alert-warning mb-0" role="alert">
                                No subscription plan is active for this partner.
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <Row className="mt-4">
                        <Col xs={12}>
                          <ButtonGroup className="flex-wrap gap-2 pb-4 px-4 partner-review-page__actions">
                            <Link
                              to="/partners"
                              className="btn btn-primary d-flex align-items-center gap-2"
                            >
                              <FaArrowLeft />
                              Back
                            </Link>
                            <Button
                              variant="outline-warning"
                              size="lg"
                              onClick={() => handleModalOpen("HOLD")}
                              className="d-flex align-items-center gap-2"
                            >
                              <FaPause />
                              Hold
                            </Button>

                            <Button
                              variant="outline-danger"
                              size="lg"
                              onClick={() => handleModalOpen("REJECT")}
                              className="d-flex align-items-center gap-2"
                            >
                              <FaTimes />
                              Reject
                            </Button>

                            {canAcceptPartner ? (
                              <Button
                                variant="outline-success"
                                size="lg"
                                onClick={() => handleModalOpen("APPROVE")}
                                className="d-flex align-items-center gap-2"
                              >
                                <FaCheck />
                                Accept
                              </Button>
                            ) : null}
                          </ButtonGroup>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            <Modal show={isModalOpen} onHide={handleModalClose} centered>
              <Modal.Header closeButton>
                <Modal.Title>
                  {modalAction === "APPROVE"
                    ? "Accept Partner"
                    : modalAction === "HOLD"
                    ? "Hold Partner"
                    : "Reject Partner"}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                Are you sure you want to{" "}
                {modalAction === "APPROVE" ? "accept" : modalAction.toLowerCase()}{" "}
                this partner?
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleModalClose}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleReviewAction}>
                  Confirm
                </Button>
              </Modal.Footer>
            </Modal>

            <Modal
              show={showModal}
              onHide={handleClose}
              size="xl"
              centered
              dialogClassName="image-zoom-modal"
            >
              <Modal.Body className="p-0 bg-dark">
                {selectedImage ? (
                  <img
                    src={selectedImage}
                    alt="Zoomed document"
                    className="img-fluid w-100"
                  />
                ) : null}
              </Modal.Body>
            </Modal>
          </Container>
        </div>
      )}
    </>
  );
};

export default PartnerItem;

const SectionHeader = ({ icon, title }) => (
  <div className="col-12 mt-4">
    <h6 className="text-uppercase text-muted d-flex align-items-center gap-2">
      <i className={`bi ${icon}`}></i> {title}
    </h6>
    <hr className="my-2" />
  </div>
);

const InfoItem = ({ label, value, icon }) => (
  <div className="col-md-6">
    <div className="d-flex align-items-start gap-2">
      <i className={`bi ${icon} text-primary`} style={{ fontSize: "1rem" }}></i>
      <div>
        <div className="text-muted small">{label}</div>
        <div className="fw-semibold text-dark">{value || "Not provided"}</div>
      </div>
    </div>
  </div>
);
